import { IconName } from '../../components/icons';

/**
 * Grouped navigation model.
 *
 * The production sidebar renders 26 `generateLink(...)` calls inline,
 * alphabetically, with no icons and no grouping — "Bay Handover" sits next to
 * "Capability" next to "Certificates", which are three unrelated jobs. Sorting
 * by name optimises for a user who already knows the exact label they want;
 * everyone else scans all 26 every time.
 *
 * Grouping by *what you are doing* cuts the scan to one section. The five
 * groups below follow the real access-group clusters in `App.tsx`.
 *
 * As data rather than JSX, this list can eventually be shared with the route
 * table in `App.tsx` — today the two are duplicated and drift apart.
 */

export type NavItem = {
  label: string;
  /** Real app route, kept accurate so this config can be promoted later. */
  path: string;
  icon: IconName;
  /** Optional count badge, e.g. items awaiting approval. */
  badge?: number;
};

export type NavSection = {
  title: string;
  items: NavItem[];
};

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Operations',
    items: [
      { label: 'Hangar Overview', path: '/hangar-overview', icon: 'airlinesRounded' },
      { label: 'Hangar Plan', path: '/HangarPlan', icon: 'scheduleOutlined' },
      { label: 'Shop Plan', path: '/ShopPlan', icon: 'build' },
      { label: 'Line KEF', path: '/line-kef', icon: 'flightTakeoff' },
      { label: 'Line RKV', path: '/line-rkv', icon: 'localAirport' },
      { label: 'Line Planner', path: '/line-planner', icon: 'formatListNumbered' },
      { label: 'Maintenance Control', path: '/maintenance-control', icon: 'alarmOn' },
      { label: 'Bay Handover', path: '/bay-handover', icon: 'send' },
    ],
  },
  {
    title: 'Workforce',
    items: [
      { label: 'Staff Management', path: '/staff-management', icon: 'groups' },
      { label: 'Task Assignment', path: '/task-assignment', icon: 'checklistRtl' },
      { label: 'Courses', path: '/courses', icon: 'school' },
      { label: 'Mechanic Info', path: '/skills', icon: 'person' },
      { label: 'Certificates', path: '/certificates', icon: 'menuBook', badge: 4 },
    ],
  },
  {
    title: 'Assets',
    items: [
      { label: 'Tools Register', path: '/tool-register', icon: 'build' },
      { label: 'Tools Stock', path: '/tool-stock', icon: 'shoppingCart' },
      { label: 'World Tools', path: '/world-tools', icon: 'public' },
      { label: 'Capability', path: '/capability', icon: 'allInclusive' },
    ],
  },
  {
    title: 'Requests',
    items: [
      { label: 'Warranty', path: '/warranty', icon: 'description' },
      { label: 'Extra Payments', path: '/extra-payments', icon: 'attachMoney', badge: 12 },
      { label: 'Clothes Requests', path: '/clothing-request', icon: 'work' },
      { label: 'Request Leave', path: '/vacations', icon: 'directionsWalk' },
    ],
  },
  {
    title: 'Admin',
    items: [
      { label: 'I/O Checklists', path: '/checklists', icon: 'checklistRtl' },
      { label: 'Access Control', path: '/access-control', icon: 'lock' },
      { label: 'Security', path: '/security', icon: 'visibility' },
      { label: 'Lite', path: '/lite', icon: 'phoneAndroidOutlined' },
    ],
  },
];
